import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, FlaskConical, UserPlus, UserMinus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';

export function DemoDataPanel({ surveyId }: { surveyId: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<null | 'add5' | 'add10' | 'rm5' | 'rm10' | 'rmAll'>(null);

  const countQ = useQuery({
    queryKey: ['admin', 'demo-user-count', surveyId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_demo_user_count', { _survey_id: surveyId });
      if (error) throw error;
      return (data as number) ?? 0;
    },
    enabled: !!surveyId,
  });

  const run = useMutation({
    mutationFn: async ({ kind, count }: { kind: 'seed' | 'remove'; count: number }) => {
      const fn = kind === 'seed' ? 'admin_seed_demo_users' : 'admin_remove_demo_users';
      const { data, error } = await supabase.rpc(fn, { _survey_id: surveyId, _count: count });
      if (error) throw error;
      return data as { created?: number; deleted?: number };
    },
    onSuccess: (data, vars) => {
      const n = (data?.created ?? data?.deleted ?? 0) as number;
      toast({
        title: vars.kind === 'seed' ? `Added ${n} demo users` : `Removed ${n} demo users`,
      });
      qc.invalidateQueries({ queryKey: ['admin', 'demo-user-count', surveyId] });
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (e: Error) => toast({ variant: 'destructive', title: 'Failed', description: e.message }),
    onSettled: () => setBusy(null),
  });

  const click = (key: typeof busy, kind: 'seed' | 'remove', count: number) => {
    setBusy(key);
    run.mutate({ kind, count });
  };

  const total = countQ.data ?? 0;

  return (
    <Card className="border-dashed">
      <CardHeader>
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-muted-foreground" />
          Demo data
          <Badge variant="outline" className="ml-1">{total} loaded</Badge>
        </CardTitle>
        <CardDescription>
          Seed synthetic employees with realistic anchor scores, 6 weeks of pulses, and conversation
          summaries. Useful for testing reports with n ≥ 5 before real respondents arrive.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button size="sm" disabled={!!busy} onClick={() => click('add5', 'seed', 5)}>
          {busy === 'add5' ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <UserPlus className="h-3.5 w-3.5 mr-2" />}
          Add 5
        </Button>
        <Button size="sm" disabled={!!busy} onClick={() => click('add10', 'seed', 10)}>
          {busy === 'add10' ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <UserPlus className="h-3.5 w-3.5 mr-2" />}
          Add 10
        </Button>
        <Button size="sm" variant="outline" disabled={!!busy || total === 0} onClick={() => click('rm5', 'remove', 5)}>
          {busy === 'rm5' ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <UserMinus className="h-3.5 w-3.5 mr-2" />}
          Remove 5
        </Button>
        <Button size="sm" variant="outline" disabled={!!busy || total === 0} onClick={() => click('rm10', 'remove', 10)}>
          {busy === 'rm10' ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <UserMinus className="h-3.5 w-3.5 mr-2" />}
          Remove 10
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive"
          disabled={!!busy || total === 0}
          onClick={() => {
            if (confirm(`Remove all ${total} demo users?`)) click('rmAll', 'remove', 0);
          }}
        >
          Remove all
        </Button>
      </CardContent>
    </Card>
  );
}
